/**
 * Noticing that the API has moved on — CLAUDE.md §6.5. Rolling deploys leave open tabs running old
 * JS against a new API; `lib/api.ts` records the first build version this tab saw and this reads
 * whether the API has since reported a different one. `useSyncExternalStore` polls a module
 * variable because the comparison already happens on every response — this is only a re-render nudge.
 */

import { useSyncExternalStore } from 'react';

import { getServerBuildVersion, hasApiBuildChanged } from '@/lib/api';

/** Slow on purpose: the prompt is an offer, not an alarm, and a mismatch stays true until reload. */
const POLL_INTERVAL_MS = 15_000;

function subscribe(onStoreChange: () => void): () => void {
  const timer = window.setInterval(onStoreChange, POLL_INTERVAL_MS);
  return () => {
    window.clearInterval(timer);
  };
}

/** The build version the API reported most recently, or null before the first call. */
export function useServerBuildVersion(): string | null {
  return useSyncExternalStore(subscribe, getServerBuildVersion, () => null);
}

/**
 * True once the API is known to be running a different build from the one this tab first reached.
 * **The comparison is against the API's own first answer, never `__BUILD_VERSION__`** (§6.5) — the
 * two repos report incomparable values, and comparing them pinned the prompt on permanently.
 */
export function useBuildVersionMismatch(): boolean {
  return useSyncExternalStore(subscribe, hasApiBuildChanged, () => false);
}
